
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import List from '@/common/components/UI/List/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import DeliveryOrderListItem from '@/modules/DispatcherModule/components/DeliveryOrderListItem/index.vue'
import DeliveryModal from '@/modules/DispatcherModule/components/DeliveryModal/index.vue'
import DeliveryVerificationModal from '@/modules/DispatcherModule/components/DeliveryVerificationModal/index.vue'
import ChangeDateModal from '@/modules/DispatcherModule/components/ChangeDateDelivery/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { dispatcher } from '@/core/realisations/dispatcher.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'

// types
import { DispatcherDeliveryListOrderT } from '@/core/types/dispatcher.types'
import { DispatcherCourierT } from '@/core/types/couriers.types'
import { DispatcherSectorT } from '@/core/types/dispatchers_sector.types'

// composable
import useHelpers from '@/common/composable/useHelpers'
import useAccount from '@/common/composable/useAccount'

// constants
import { DELIVERY_STATUSES } from '@/core/constants/deliveries.constants'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        list: List,
        'the-select': TheSelect,
        'the-input': TheInput,
        'the-button': TheButton,
        //
        'delivery-order-list-item': DeliveryOrderListItem,
        'delivery-modal': DeliveryModal,
        'delivery-verification-modal': DeliveryVerificationModal,
        'change-date-modal': ChangeDateModal,
    },
    setup() {
        const account = useAccount()
        const user_id = account.state.user_id

        const state = reactive(new DataState<DispatcherDeliveryListOrderT>({}))

        const filters_state = reactive({
            dispatchers_sector_id: null as number | null,
            courier_id: null as number | null,
            invoice_number: null as string | null,
            order_id: null,
            order_number_1c: null,
            status: null as number | null,
        })

        const delivery_close_state = reactive({
            opened: false,
            delivery_id: null as number | null,
        })

        const change_date_state = reactive({
            is_changing_date: false,
            order_id: null as number | null,
            delivery_current: null as DispatcherDeliveryListOrderT | null,
        })

        const verification_state = reactive({
            opened: false,
            delivery_id: null as number | null,
            company_id: null as number | null,
            invoice_number: null as string | null,
            phone_number: null as string | null,
        })

        const loadDeliveryList = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const response = await dispatcher.getDeliveryList({
                    page,
                    dispatcher_sector_id: filters_state.dispatchers_sector_id,
                    courier_id: filters_state.courier_id,
                    invoice_number: filters_state.invoice_number,
                    order_id: filters_state.order_id,
                    order_number_1c: filters_state.order_number_1c,
                    deliveryStatus: filters_state.status,
                })

                state.list = response.data
                state.pagination.total_pages = response.meta.last_page

                state.is_failed = false
                state.is_completed = true
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const data_state = reactive({
            couriers: new DataState<DispatcherCourierT>({}),
            dispatchers_sectors: new DataState<DispatcherSectorT>({}),
        })

        const loadCouriers = async (page: number, search?: string) => {
            data_state.couriers.pagination.current_page = page

            try {
                data_state.couriers.is_loading = true

                const response = await couriers.getList({
                    page,
                    name: search,
                    statusIds: [2],
                })

                data_state.couriers.list = response.data
                data_state.couriers.pagination.total_pages = response.meta.last_page

                data_state.couriers.is_failed = false
                data_state.couriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.couriers.failure.catchError(error)

                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_failed = true
                }
            } finally {
                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_loading = false
                }
            }
        }

        const loadMoreCouriers = async (page: number) => {
            if (page > data_state.couriers.pagination.total_pages) return
            data_state.couriers.pagination.current_page = page

            try {
                data_state.couriers.is_loading = true

                const response = await couriers.getList({ page, statusIds: [2] })
                data_state.couriers.list = data_state.couriers.list.concat(response.data)

                data_state.couriers.pagination.total_pages = response.meta.last_page

                data_state.couriers.is_failed = false
                data_state.couriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.couriers.failure.catchError(error)

                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_failed = true
                }
            } finally {
                if (!data_state.couriers.failure.error_state.is_cancel) {
                    data_state.couriers.is_loading = false
                }
            }
        }

        const searchCourier = async (search_query: string) => {
            // data_state.countries.pagination.current_page
            loadCouriers(1, search_query)
        }

        const unshiftFindedCourier = async (courier: DispatcherCourierT) => {
            await loadCouriers(data_state.couriers.pagination.current_page)
            data_state.couriers.list.unshift(courier)
        }

        const loadDispatchersSectors = async () => {
            try {
                data_state.dispatchers_sectors.is_loading = true

                const response = await dispatchers_sectors.getList()
                data_state.dispatchers_sectors.list = response.data

                data_state.dispatchers_sectors.is_failed = false
                data_state.dispatchers_sectors.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.dispatchers_sectors.failure.catchError(error)

                if (!data_state.dispatchers_sectors.failure.error_state.is_cancel) {
                    data_state.dispatchers_sectors.is_failed = true
                }
            } finally {
                if (!data_state.dispatchers_sectors.failure.error_state.is_cancel) {
                    data_state.dispatchers_sectors.is_loading = false
                }
            }
        }

        onBeforeMount(async () => {
            state.is_loading = true

            loadCouriers(1)
            await loadDispatchersSectors()

            // Автозаполнение сектора
            if (data_state.dispatchers_sectors.list) {
                let auto_complete_sector = data_state.dispatchers_sectors.list.find(
                    (s) => s.users && s.users.find((user) => user.id === user_id)
                )
                if (auto_complete_sector) {
                    filters_state.dispatchers_sector_id = auto_complete_sector.id
                }
            }

            loadDeliveryList(1)
        })

        const { findItemIndexById } = useHelpers()

        const onOrderRemoved = (order_id: number) => {
            const index = findItemIndexById(order_id, state.list)
            if (index === -1) return

            state.list.splice(index, 1)
        }

        const onFiltersReset = () => {
            filters_state.dispatchers_sector_id = null
            filters_state.courier_id = null
            filters_state.invoice_number = null
            filters_state.status = null

            loadDeliveryList(state.pagination.current_page)
        }

        const startDeliveryClose = (order: DispatcherDeliveryListOrderT) => {
            delivery_close_state.opened = true
            delivery_close_state.delivery_id = order.id
        }

        const cancelDeliveryClose = () => {
            delivery_close_state.opened = false
            delivery_close_state.delivery_id = null
        }

        const onDeliveryClosed = () => {
            cancelDeliveryClose()
            loadDeliveryList(state.pagination.current_page)
        }

        const startDeliveryVerification = (order: DispatcherDeliveryListOrderT) => {
            verification_state.delivery_id = order.id
            verification_state.company_id = order.company_id
            verification_state.invoice_number = order.invoice_number
            verification_state.phone_number = order.phone

            verification_state.opened = true
        }

        const cancelDeliveryVerification = () => {
            verification_state.opened = false
            verification_state.delivery_id = null
            verification_state.company_id = null
            verification_state.invoice_number = null
            verification_state.phone_number = null
        }

        const onDeliveryVerificationClosed = () => {
            cancelDeliveryVerification()
            loadDeliveryList(state.pagination.current_page)
        }

        const toggleChangeDate = () => {
            change_date_state.is_changing_date = !change_date_state.is_changing_date
        }

        const onChangeDate = (delivery_current: DispatcherDeliveryListOrderT) => {
            change_date_state.delivery_current = delivery_current
            toggleChangeDate()
        }

        const onDateChanged = () => {
            loadDeliveryList(1)
            toggleChangeDate()
        }

        return {
            state,
            filters_state,
            data_state,
            change_date_state,
            //
            loadDeliveryList,
            onOrderRemoved,
            loadCouriers,
            loadMoreCouriers,
            searchCourier,
            unshiftFindedCourier,
            loadDispatchersSectors,
            onFiltersReset,
            DELIVERY_STATUSES,
            delivery_close_state,
            startDeliveryClose,
            cancelDeliveryClose,
            onDeliveryClosed,
            verification_state,
            startDeliveryVerification,
            cancelDeliveryVerification,
            onDeliveryVerificationClosed,
            toggleChangeDate,
            onChangeDate,
            onDateChanged,
        }
    },
})
